import React from "react";
import { Eye, Target, ShieldCheck, ArrowRight } from "lucide-react";

const Stitch = ({ className = "", color = "#2E7D32" }) => (
  <svg className={className} viewBox="0 0 100 4" preserveAspectRatio="none">
    <line
      x1="0" y1="2" x2="100" y2="2"
      stroke={color} strokeWidth="2" strokeDasharray="6 5" strokeLinecap="round"
    />
  </svg>
);

const pillars = [
  {
    icon: Eye,
    title: "Our Vision",
    tag: "01",
    text: "To create an inclusive and self-reliant society by empowering people through quality education, employable skills, sustainable livelihoods, and community development.",
  },
  {
    icon: Target,
    title: "Our Mission",
    tag: "02",
    text: "To provide opportunities for skill development, vocational education, entrepreneurship, employment generation, and social upliftment for women, youth, rural communities, minorities, tribal populations, and other disadvantaged groups.",
  },
  {
    icon: ShieldCheck,
    title: "Our Approach",
    tag: "03",
    text: "The Trust collaborates with government departments, industries, and development partners to design and deliver high-impact training programs that directly improve employability and quality of life.",
  },
];

const About = () => {
  return (
    <section id="about" className="py-24 bg-[#F2ECDD] scroll-mt-20">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,500;9..144,600;9..144,700&family=IBM+Plex+Sans:wght@400;500;600&family=IBM+Plex+Mono:wght@500&display=swap');
        .mbf-display { font-family: 'Fraunces', serif; }
        .mbf-body { font-family: 'IBM Plex Sans', sans-serif; }
        .mbf-mono { font-family: 'IBM Plex Mono', monospace; }
      `}</style>

      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Top Header */}
        <div className="max-w-3xl mx-auto mb-16 text-center">
          <span className="mbf-mono text-[12px] tracking-[0.18em] uppercase text-[#2E7D32]">
            About the Foundation
          </span>
          <h2 className="mbf-display mt-4 text-4xl font-semibold text-[#0A355C] leading-tight tracking-tight">
            Empowering Lives Since September 2015
          </h2>
          <Stitch className="w-24 h-2 mx-auto mt-5" color="#2E7D32" />
          <p className="mbf-body mt-6 text-[#514a3d] leading-8">
            M.B. Foundation of Skill Development &amp; Livelihood (MBFSDL) is
            a registered charitable public trust. We operate as a non-profit,
            non-political, and voluntary organization dedicated to serving
            society without any profit motive.
          </p>
        </div>

        {/* Side-by-Side Content */}
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left Side: About Image */}
          <div className="lg:col-span-5 relative">
            <div className="relative border border-[#0A355C]/15 p-3 bg-white">
              <div className="overflow-hidden">
                <img
                  src="/images/about.jpg"
                  alt="Women empowerment workshop by MBFSDL"
                  className="w-full h-[420px] object-cover"
                />
              </div>
              <span className="absolute -top-[3px] -left-[3px] w-4 h-4 border-t-2 border-l-2 border-[#2E7D32]" />
              <span className="absolute -bottom-[3px] -right-[3px] w-4 h-4 border-b-2 border-r-2 border-[#2E7D32]" />
            </div>

            {/* Overlay badge, tag-shaped */}
            <div
              className="absolute -bottom-6 -right-6 bg-[#0A355C] text-white p-6 max-w-[200px] shadow-xl"
              style={{ clipPath: "polygon(0 0, 100% 0, 100% 82%, 84% 100%, 0 100%)" }}
            >
              <h4 className="mbf-display text-4xl font-semibold text-[#E0A526]">
                10+
              </h4>
              <p className="mbf-body text-sm font-semibold mt-1">
                Years of Impact
              </p>
              <p className="mbf-body text-xs text-blue-100/60 mt-0.5">
                Serving underserved communities
              </p>
            </div>
          </div>

          {/* Right Side: Vision, Mission, Approach — ledger-style cards */}
          <div className="lg:col-span-7 space-y-4">
            {pillars.map(({ icon: Icon, title, tag, text }) => (
              <div
                key={title}
                className="relative bg-white border border-[#0A355C]/10 pl-6 pr-6 py-6 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-[#0A355C]/5 transition-all duration-300"
              >
                <span className="absolute left-0 top-0 bottom-0 w-[3px] bg-[#2E7D32]" />
                <div className="flex gap-5 items-start">
                  <div className="w-11 h-11 rounded-full border-2 border-[#0A355C] flex items-center justify-center text-[#0A355C] shrink-0">
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-baseline gap-3">
                      <h3 className="mbf-display text-xl font-semibold text-[#0A355C]">
                        {title}
                      </h3>
                      <span className="mbf-mono text-[11px] text-[#2E7D32]/70">
                        {tag}
                      </span>
                    </div>
                    <p className="mbf-body mt-2 text-[#514a3d] leading-relaxed text-[14.5px]">
                      {text}
                    </p>
                  </div>
                </div>
              </div>
            ))}

            <div className="mt-8 pt-2 flex justify-start lg:justify-end">
              <a
                href="#about"
                className="mbf-body inline-flex items-center gap-2 bg-[#0A355C] hover:bg-[#082440] text-white px-6 py-3.5 font-semibold text-xs uppercase tracking-wider transition shadow-sm"
              >
                Read Our Full Story
                <ArrowRight className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;